import { useApp } from "@/context/AppContext";
import { brl, fmtDateTime, fmtDay, fileUrl } from "@/lib/api";
import { ACEITE, ATENDIMENTO, BRAND, DESLOCAMENTO, GARANTIA, PAGAMENTO, RESSALVAS, STATUS_LABEL } from "@/lib/content";
import { VehicleDiagram } from "@/components/site/VehicleDiagram";

const H = ({ n, children }) => (
  <h3 className="mb-3 mt-8 flex items-center gap-2 border-b-2 border-slate-900 pb-1 font-heading text-sm font-bold uppercase tracking-wider text-slate-900">
    <span className="bg-[#DC2626] px-1.5 font-mono text-xs text-white">{n}</span>{children}
  </h3>
);

const KV = ({ k, v, testid }) => (
  <div className="border-b border-slate-200 py-1.5 text-[13px]">
    <span className="block font-mono text-[10px] uppercase tracking-wider text-slate-500">{k}</span>
    <span className="text-slate-900" data-testid={testid}>{v || "—"}</span>
  </div>
);

const Row = ({ k, v, strong, red, testid }) => (
  <div className={`flex justify-between gap-4 border-b border-slate-200 py-1.5 text-sm ${strong ? "font-bold" : ""}`}>
    <span className="text-slate-700">{k}</span>
    <span className={red ? "text-[#DC2626]" : "text-slate-900"} data-testid={testid}>{v}</span>
  </div>
);

const OptionsForm = ({ selected }) => {
  const { config } = useApp();
  const items = config?.items || [];
  return (
    <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2" data-testid="os-options-form">
      {["R", "I", "D", "P"].map((g) => (
        <div key={g} className="avoid-break">
          <p className="mb-1 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-500">{config?.groups?.[g]}</p>
          {items.filter((i) => i.group === g).map((i) => {
            const on = selected.includes(i.key);
            return (
              <div key={i.key} className={`flex items-center gap-2 text-[12px] ${on ? "font-semibold text-slate-900" : "text-slate-500"}`}>
                <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center border text-[9px] ${on ? "border-[#DC2626] bg-[#DC2626] text-white" : "border-slate-400"}`}>{on ? "✓" : ""}</span>
                <span className="font-mono">{i.code}</span> {i.nome}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const Clientes = ({ os }) => {
  const c = os.cliente, v = os.veiculo;
  return (
    <div className="grid gap-x-8 sm:grid-cols-2">
      <div>
        <H n="01">Cliente</H>
        <KV k="Nome" v={c.nome} testid="os-cliente-nome" /><KV k="Telefone / WhatsApp" v={`${c.telefone}${c.whatsapp ? ` / ${c.whatsapp}` : ""}`} />
        <KV k="E-mail" v={c.email} /><KV k="Endereço" v={c.endereco} />
        <KV k="Cidade / Estado / CEP" v={`${c.cidade} / ${c.estado}${c.cep ? ` / ${c.cep}` : ""}`} />
      </div>
      <div>
        <H n="02">Veículo</H>
        <KV k="Marca / Modelo / Versão" v={`${v.marca} ${v.modelo} ${v.versao || ""}`} testid="os-veiculo" />
        <KV k="Ano / Cor / Placa" v={`${v.ano || "—"} / ${v.cor || "—"} / ${v.placa || "—"}`} />
        <KV k="Quilometragem" v={v.quilometragem} />
        <KV k="Características" v={[v.origem, v.blindado ? "Blindado" : "Não blindado", v.eletrico && "Elétrico", v.hibrido && "Híbrido"].filter(Boolean).join(" · ")} />
      </div>
    </div>
  );
};

const Valores = ({ os }) => {
  const val = os.valores;
  return (
    <div className="grid gap-x-8 sm:grid-cols-2">
      <div>
        <Row k="Material" v={brl(val.material)} />
        <Row k="Mão de obra" v={brl(val.mao_de_obra)} />
        <Row k="Custo do serviço" v={brl(val.custo_servico)} testid="os-custo-servico" />
        <Row k="Custo estimado de deslocamento" v={val.deslocamento == null ? "A definir pelo prestador" : brl(val.deslocamento)} testid="os-deslocamento" />
        <Row k="Desconto" v={`- ${brl(val.desconto)}`} />
        <Row k="VALOR TOTAL" v={brl(val.total)} strong red testid="os-total" />
        {!val.precificado && <p className="mt-2 text-[11px] text-amber-700" data-testid="os-pending-note">Valor parcial: o total final será apresentado após a definição dos preços e do deslocamento pelo prestador.</p>}
      </div>
      <div>
        <Row k="Forma de pagamento" v={os.forma_pagamento} testid="os-pagamento" />
        <Row k={`Sinal (${val.sinal_percent}%)`} v={brl(val.sinal)} />
        <Row k="Saldo" v={os.fora_municipio ? `${brl(val.saldo)} — a combinar` : brl(val.saldo)} />
        <p className="mt-2 text-[11px] leading-relaxed text-slate-600">
          {os.fora_municipio ? `Atendimento fora do município do Rio de Janeiro: sinal de ${PAGAMENTO.fora.sinal}, saldo ${PAGAMENTO.fora.saldo.toLowerCase()}. ` : ""}
          Bancos e bandeiras: {PAGAMENTO.bancos.join(", ")}.
        </p>
      </div>
    </div>
  );
};

const Inspecao = ({ os }) => {
  const i = os.inspecao || {};
  return (
    <div className="grid gap-6 sm:grid-cols-[1fr_220px]">
      <div>
        <KV k="Nível do tanque de combustível" v={i.tanque} /><KV k="Condições dos pneus" v={i.pneus} />
        <KV k="Condições dos bancos" v={i.bancos} /><KV k="Estado do interior" v={i.interior} />
        <KV k="Estado externo" v={i.externo} /><KV k="Danos previamente declarados" v={i.danos_declarados} />
        <KV k="Danos encontrados na inspeção" v={i.danos_encontrados} />
        {os.marcacoes?.length > 0 && (
          <ol className="mt-2 space-y-0.5 text-[12px] text-slate-700">
            {os.marcacoes.map((m, k) => <li key={k}><b>{k + 1}.</b> {m.tipo === "declarado" ? "Declarado" : "Encontrado"}{m.nota ? ` — ${m.nota}` : ""}</li>)}
          </ol>
        )}
      </div>
      <VehicleDiagram marks={os.marcacoes || []} light />
    </div>
  );
};

const Termos = () => (
  <div className="space-y-3 text-[12px] leading-relaxed text-slate-700">
    <p><b>Não inclusos:</b> {RESSALVAS.nao_inclusos.join("; ")}.</p>
    <p><b>Não há responsabilidade sobre:</b> {RESSALVAS.nao_responsabilidade.join("; ")}.</p>
    <p>{RESSALVAS.observacao}</p>
    <p><b>Deslocamento:</b> {DESLOCAMENTO.observacao} {DESLOCAMENTO.domicilio}</p>
    <p><b>Local de atendimento:</b> preferencialmente {ATENDIMENTO.preferencial.join(", ").toLowerCase()}. {ATENDIMENTO.domicilio} Condições do local: {ATENDIMENTO.condicoes_local.join(", ").toLowerCase()}. {ATENDIMENTO.observacao}</p>
    <p><b>Reserva:</b> {ACEITE.advertencia}</p>
  </div>
);

export const OSDocument = ({ os }) => (
  <article className="print-doc mx-auto max-w-4xl bg-white p-6 text-slate-900 shadow-2xl sm:p-10" data-testid="os-document">
    <header className="flex flex-wrap items-start justify-between gap-4 border-b-4 border-[#DC2626] pb-5">
      <OSBrand />
      <div className="text-right">
        <p className="font-heading text-lg font-bold uppercase">Ordem de Serviço - Nillo Ruídos</p>
        <p className="font-mono text-2xl font-bold text-[#DC2626]" data-testid="os-numero">{os.numero}</p>
        <p className="font-mono text-[11px] uppercase text-slate-500" data-testid="os-status">{STATUS_LABEL[os.status]}</p>
      </div>
    </header>
    <div className="mt-4 grid grid-cols-2 gap-x-6 sm:grid-cols-4">
      <KV k="Data de emissão" v={fmtDateTime(os.data_emissao)} /><KV k="Data pretendida" v={fmtDay(os.data_pretendida)} />
      <KV k="Visita técnica" v={fmtDay(os.data_visita)} /><KV k="Prestação do serviço" v={fmtDay(os.data_servico)} />
    </div>
    <Clientes os={os} />
    <H n="03">Serviços selecionados</H>
    <table className="w-full text-[13px]" data-testid="os-services-table">
      <thead><tr className="bg-slate-900 text-left font-mono text-[10px] uppercase tracking-wider text-white">
        <th className="p-2">Cód.</th><th className="p-2">Serviço</th><th className="p-2 text-right">Material</th><th className="p-2 text-right">Mão de obra</th><th className="p-2 text-right">Preço</th>
      </tr></thead>
      <tbody>{os.linhas.map((l) => (
        <tr key={l.key} className="border-b border-slate-200" data-testid={`os-line-${l.key}`}>
          <td className="p-2 font-mono text-[#DC2626]">{l.code}</td><td className="p-2">{l.nome}</td>
          <td className="p-2 text-right">{brl(l.material)}</td><td className="p-2 text-right">{brl(l.mao_de_obra)}</td><td className="p-2 text-right font-semibold">{brl(l.preco)}</td>
        </tr>))}
      </tbody>
    </table>
    <H n="04">Descrição operacional</H>
    <ol className="list-decimal space-y-1.5 pl-5 text-[13px] leading-relaxed" data-testid="os-descricao-operacional">
      {os.descricao_operacional.map((d, i) => <li key={i}>{d}</li>)}
    </ol>
    <H n="05">Formulário de opções</H>
    <OptionsForm selected={os.servicos} />
    <H n="06">Valores e pagamento</H>
    <Valores os={os} />
    <H n="07">Condições do veículo</H>
    <Inspecao os={os} />
    {os.fotos_meta?.length > 0 && (
      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="os-photos">
        {os.fotos_meta.map((f) => <img key={f.id} src={fileUrl(f.id)} alt="Registro fotográfico" className="avoid-break aspect-square w-full border border-slate-300 object-cover" />)}
      </div>
    )}
    <H n="08">Observações</H>
    <p className="text-[13px]"><b>Local de atendimento:</b> {os.local_atendimento || "—"}</p>
    <p className="text-[13px]"><b>Cliente:</b> {os.observacoes || "—"}</p>
    {os.observacoes_prestador && <p className="text-[13px]"><b>Prestador:</b> {os.observacoes_prestador}</p>}
    <H n="09">Condições contratuais</H>
    <Termos />
    <H n="10">{GARANTIA.titulo}</H>
    <p className="text-[13px]">{GARANTIA.texto}</p>
    <ul className="mt-1 list-disc pl-5 text-[12px] text-slate-700">{GARANTIA.condicoes.map((c) => <li key={c}>{c}</li>)}</ul>
    <H n="11">{ACEITE.titulo}</H>
    {os.aceite ? (
      <div className="border-2 border-green-700 bg-green-50 p-4 text-[13px]" data-testid="os-aceite-registro">
        <p className="font-bold text-green-800">ACEITO O ORÇAMENTO E AUTORIZO A RESERVA</p>
        <p>Nome: <b>{os.aceite.nome}</b>{os.aceite.documento ? ` · Doc.: ${os.aceite.documento}` : ""}</p>
        <p>Data e hora: {fmtDateTime(os.aceite.data_hora)} · IP: {os.aceite.ip || "não disponível"}</p>
        <p>Valor aceito: {brl(os.aceite.total_aceito)}</p>
      </div>
    ) : <p className="text-[13px] text-slate-500">Aceite digital ainda não registrado.</p>}
    <footer className="mt-10 border-t border-slate-300 pt-4 text-center font-mono text-[11px] uppercase tracking-wider text-slate-500">
      Nillo Ruídos — {BRAND.slogan} · WhatsApp {BRAND.telefone} · {BRAND.site.replace("https://", "")} · {BRAND.instagram}
    </footer>
  </article>
);

const OSBrand = () => {
  const { config } = useApp();
  const id = config?.branding?.logo_file_id;
  if (id) return <img src={fileUrl(id)} alt="Nillo Ruídos" className="h-14 w-auto object-contain" />;
  return (
    <div className="flex items-center gap-3">
      <img src="/img/logo-nillo.jpg" alt="Equipe Nillo Ruídos" className="h-16 w-16 rounded-full object-cover" />
      <div>
      <p className="font-heading text-2xl font-bold tracking-wider">NILLO <span className="text-[#DC2626]">RUÍDOS</span></p>
      <p className="text-[11px] text-slate-600">Tratamento de ruídos e isolamento termoacústico</p>
      <p className="font-mono text-[11px] text-slate-600">WhatsApp {BRAND.telefone}</p>
      </div>
    </div>
  );
};
